import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { pageTitle } from '../../helper'
import Button from '../Button'
import Cta from '../Cta'
import IconBox from '../IconBox'
import PageHeading from '../PageHeading'
import Div from '../Div'
import SectionHeading from '../SectionHeading'
import TestimonialSlider from '../Slider/TestimonialSlider'
import Spacing from '../Spacing'

const servicesData = {
  'web-solutions': {
    title: 'Web Solutions',
    subtitle: 'Modern Web Applications',
    description: 'We design and develop modern web applications that are secure, scalable, and aligned with business objectives.',
    process: [
      { icon: '/images/icons/service_icon_1.svg', title: 'Planning & Architecture', subtitle: 'We analyze your business needs and define the technical architecture for a scalable, maintainable web solution.' },
      { icon: '/images/icons/service_icon_2.svg', title: 'Development & Integration', subtitle: 'We build robust web applications with clean code, API integrations, and third-party service connections.' },
      { icon: '/images/icons/service_icon_3.svg', title: 'Deployment & Optimization', subtitle: 'We deploy your application with performance optimization, SEO-friendly architecture, and ongoing support.' },
    ],
    features: [
      'Corporate websites and SaaS platforms',
      'Admin panels, dashboards, and internal systems',
      'E-commerce and marketplace solutions',
      'API-driven web applications',
      'Third-party service and system integrations',
      'Performance optimization and SEO-friendly architecture',
    ],
  },
  'mobile-solutions': {
    title: 'Mobile Solutions',
    subtitle: 'Native Mobile Applications',
    description: 'We build native mobile applications focused on performance, usability, and long-term reliability.',
    process: [
      { icon: '/images/icons/service_icon_1.svg', title: 'Research & UX Design', subtitle: 'We research user behavior and design intuitive mobile experiences for iOS and Android platforms.' },
      { icon: '/images/icons/service_icon_2.svg', title: 'Native Development', subtitle: 'We develop native iOS (Swift) and Android (Kotlin) applications with secure backend and API integration.' },
      { icon: '/images/icons/service_icon_3.svg', title: 'Launch & Support', subtitle: 'We handle App Store and Google Play deployment, with ongoing maintenance and feature updates.' },
    ],
    features: [
      'Native iOS (Swift) and Android (Kotlin) development',
      'Customer-facing and business mobile applications',
      'Secure backend and API integration',
      'Offline support and real-time functionality',
      'App Store and Google Play deployment support',
    ],
  },
  'enterprise-solutions': {
    title: 'Enterprise Solutions',
    subtitle: 'Enterprise-Grade Systems',
    description: 'We deliver enterprise-grade systems designed to support complex business processes and organizational growth.',
    process: [
      { icon: '/images/icons/service_icon_1.svg', title: 'Business Analysis', subtitle: 'We understand your organization\'s workflows and define the technical requirements for enterprise-level systems.' },
      { icon: '/images/icons/service_icon_2.svg', title: 'System Development', subtitle: 'We build scalable backend architectures, microservices, and high-availability systems with robust security.' },
      { icon: '/images/icons/service_icon_3.svg', title: 'Modernization & Scaling', subtitle: 'We modernize legacy systems and implement role-based access control for long-term operational efficiency.' },
    ],
    features: [
      'Custom enterprise and internal business systems',
      'Scalable backend architectures and microservices',
      'High-availability and high-load systems',
      'Legacy system modernization',
      'Role-based access control and system security',
    ],
  },
  'app-development': {
    title: 'Application Development & Processing Systems',
    subtitle: 'Backend & Processing Architecture',
    description: 'We specialize in developing backend and application architectures capable of handling complex processing and business-critical workflows.',
    process: [
      { icon: '/images/icons/service_icon_1.svg', title: 'Workflow Analysis', subtitle: 'We map out your business-critical workflows and identify the optimal processing architecture.' },
      { icon: '/images/icons/service_icon_2.svg', title: 'System Implementation', subtitle: 'We build transaction processing, event-driven workflows, and queue-based systems for high throughput.' },
      { icon: '/images/icons/service_icon_3.svg', title: 'Monitoring & Audit', subtitle: 'We implement logging, monitoring, and audit-ready system design for production reliability.' },
    ],
    features: [
      'Transaction and request processing systems',
      'Asynchronous and event-driven workflows',
      'Background jobs and queue-based processing',
      'High-throughput and performance-critical services',
      'Logging, monitoring, and audit-ready system design',
    ],
  },
  'billing-payments': {
    title: 'Billing & Payment Integrations',
    subtitle: 'Payment System Integration',
    description: 'We design and integrate billing and payment components as part of larger software systems, ensuring reliable and predictable transaction flows.',
    process: [
      { icon: '/images/icons/service_icon_1.svg', title: 'Payment Architecture', subtitle: 'We design secure payment flows with gateway integrations and billing logic for one-time and recurring charges.' },
      { icon: '/images/icons/service_icon_2.svg', title: 'Integration & Testing', subtitle: 'We integrate webhooks, callbacks, and event-based systems with error handling and retry mechanisms.' },
      { icon: '/images/icons/service_icon_3.svg', title: 'Reconciliation & Security', subtitle: 'We implement reconciliation mechanisms and secure API-based data exchange for long-term maintainability.' },
    ],
    features: [
      'Payment gateway and third-party provider integrations',
      'Billing systems for one-time and recurring charges',
      'Subscription management and invoicing logic',
      'Webhooks, callbacks, and event-based integrations',
      'Error handling, retries, and reconciliation mechanisms',
      'Secure API-based data exchange',
    ],
  },
  'other-it-solutions': {
    title: 'Other IT Solutions',
    subtitle: 'IT Services & Consulting',
    description: 'We provide additional IT services to support complex technical and business requirements.',
    process: [
      { icon: '/images/icons/service_icon_1.svg', title: 'Technical Assessment', subtitle: 'We audit your existing systems and identify opportunities for improvement and optimization.' },
      { icon: '/images/icons/service_icon_2.svg', title: 'Strategy & Roadmap', subtitle: 'We create technology roadmaps and define API-first integration strategies for your business.' },
      { icon: '/images/icons/service_icon_3.svg', title: 'Implementation & Support', subtitle: 'We implement cloud and DevOps solutions with ongoing performance optimization and consulting.' },
    ],
    features: [
      'System design and technical architecture',
      'API-first integration strategies',
      'Cloud and DevOps solutions',
      'Performance optimization and technical audits',
      'IT consulting and technology roadmapping',
    ],
  },
};

const defaultService = servicesData['web-solutions'];

export default function ServiceDetailsPage() {
  pageTitle('Service Details');
  const params = useParams();
  const service = servicesData[params.serviceDetailsId] || defaultService;

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  return (
    <>
      <PageHeading 
        title={service.title}
        bgSrc='/images/service_hero_bg.jpeg'
        pageLinkText={service.title}
      />
      <Spacing lg='145' md='80'/>
      <Div className="container">
        <SectionHeading 
          title={service.description}
          subtitle={service.subtitle}
          variant='cs-style1 text-center'
        />
        <Spacing lg='90' md='45'/>
        <Div className="row">
          {service.process.map((item, index) => (
            <Div className="col-lg-4" key={index}>
              <IconBox
                icon={item.icon}
                title={item.title}
                subtitle={item.subtitle}
              />
              <Spacing lg='30' md='30'/>
            </Div>
          ))}
        </Div>
      </Div>
      <Spacing lg='120' md='50'/>
      <Div className="container">
        <Div className="row align-items-center">
          <Div className="col-xl-5 col-lg-6">
            <Div className="cs-radius_15 cs-shine_hover_1">
              <img src="/images/service_img_1.jpeg" alt="Service" className='cs-radius_15 w-100' />
            </Div>
            <Spacing lg='0' md='40'/>
          </Div>
          <Div className="col-lg-6 offset-xl-1">
            <h2 className="cs-font_50 cs-m0">Our services include</h2>
            <Spacing lg='50' md='30'/>
            <Div className="row">
              <Div className="col-lg-6">
                {service.features.slice(0, Math.ceil(service.features.length / 2)).map((feature, index) => (
                  <React.Fragment key={index}>
                    <Button btnLink='/contact' btnText={feature} variant='cs-type2'/>
                    <Spacing lg='20' md='10'/>
                  </React.Fragment>
                ))}
              </Div>
              <Div className="col-lg-6">
                {service.features.slice(Math.ceil(service.features.length / 2)).map((feature, index) => (
                  <React.Fragment key={index}>
                    <Button btnLink='/contact' btnText={feature} variant='cs-type2'/>
                    <Spacing lg='20' md='10'/>
                  </React.Fragment>
                ))}
              </Div>
            </Div>
          </Div>
        </Div>
      </Div>
      <Spacing lg='150' md='80'/>
      <TestimonialSlider />
      <Spacing lg='150' md='80'/>
      <Div className="container">
        <Cta 
          title={"Have a project in mind? <br />Let's <i>build</i> it together"}
          btnText='Get In Touch' 
          btnLink='/contact' 
          bgSrc='/images/cta_bg.jpeg'
        />
      </Div>
    </>
  )
}
